export const AUTH = {
  LOGIN: '/auth/login',
  LOGIN_OPTIONS: '/auth/login/options',
  LOGIN_COMPLETE: '/auth/login/complete',
  LOGOUT: '/auth/logout',
  ME: '/auth/me',
  REFRESH: '/auth/refresh',
  CHANGE_PASSWORD: '/auth/change-password',
  FORGOT_PASSWORD: '/auth/forgot-password',
  SET_PASSWORD: '/auth/set-password',
  SIGNUP: '/auth/signup',
  CHECK_SLUG: '/auth/check-slug',
  SESSIONS: '/auth/sessions',
  SIGN_OUT_OTHER_SESSIONS: '/auth/sessions/sign-out-others',
  session: (sessionId: string) => `/auth/sessions/${encodeURIComponent(sessionId)}`,
  forceLogout: (userId: string) => `/auth/users/${userId}/force-logout`,
} as const;

export const ORGANIZATIONS = {
  CURRENT: '/organizations/current',
  CURRENT_CONTACT: '/organizations/current/contact',
  CUSTOM_DOMAIN: '/organizations/current/custom-domain',
  CUSTOM_DOMAIN_VERIFY: '/organizations/current/custom-domain/verify',
  CURRENT_LOGO: '/organizations/current/logo',
} as const;

/** Public face of a tenant, looked up from its subdomain before sign-in. */
export const PUBLIC_TENANTS = {
  bySlug: (slug: string) => `/public/tenants/${encodeURIComponent(slug)}`,
} as const;

export const USERS = '/users';
export const CANDIDATES = '/candidates';

/** Current user profile (used for permissions and UI access). */
export const USERS_ME = '/users/me';
export const USERS_ME_AVATAR = '/users/me/avatar';

export function userByIdPath(id: string): string {
  return `${USERS}/${id}`;
}

export function userRecoverPath(id: string): string {
  return `${USERS}/${id}/recover`;
}

export function userStatusPath(id: string): string {
  return `${USERS}/${id}/status`;
}

export function userConfirmPermanentPath(id: string): string {
  return `${USERS}/${id}/confirm-permanent`;
}

export function userTerminatePath(id: string): string {
  return `${USERS}/${id}/terminate`;
}

export function userRolePath(id: string): string {
  return `${USERS}/${id}/role`;
}

export function userCompensationPath(id: string): string {
  return `${USERS}/${id}/compensation`;
}

export function userEmploymentPath(id: string): string {
  return `${USERS}/${id}/employment`;
}

export function userResetPasswordPath(id: string): string {
  return `${USERS}/${id}/reset-password`;
}

export function candidateByIdPath(id: string): string {
  return `${CANDIDATES}/${id}`;
}

export function candidateDocumentsPath(id: string): string {
  return `${CANDIDATES}/${id}/documents`;
}

export function candidateDocumentByIdPath(id: string, documentId: string): string {
  return `${candidateDocumentsPath(id)}/${documentId}`;
}

export function candidateRejectPath(id: string): string {
  return `${CANDIDATES}/${id}/reject`;
}

export function candidateHirePath(id: string): string {
  return `${CANDIDATES}/${id}/hire`;
}

export const COMPANIES = '/companies';
export const ME_COMPANIES = '/me/companies';
export const ORGANIZATION_DASHBOARD = '/companies/dashboard/organization';

export function companyByIdPath(id: string): string {
  return `${COMPANIES}/${id}`;
}

export function companyDashboardPath(id: string): string {
  return `${COMPANIES}/${id}/dashboard`;
}

export function companyMembersPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/members`;
}

export function companyMemberPath(companyId: string, userId: string): string {
  return `${COMPANIES}/${companyId}/members/${userId}`;
}

export function companyAttendancePath(companyId: string): string {
  return `${COMPANIES}/${companyId}/attendance`;
}

export function companyAttendanceTodayPath(companyId: string): string {
  return `${companyAttendancePath(companyId)}/today`;
}

export function companyAttendanceClockInPath(companyId: string): string {
  return `${companyAttendancePath(companyId)}/clock-in`;
}

export function companyAttendanceBreakStartPath(companyId: string): string {
  return `${companyAttendancePath(companyId)}/break/start`;
}

export function companyAttendanceBreakEndPath(companyId: string): string {
  return `${companyAttendancePath(companyId)}/break/end`;
}

export function companyAttendanceClockOutPath(companyId: string): string {
  return `${companyAttendancePath(companyId)}/clock-out`;
}

export function companyAttendanceEndDayPath(companyId: string): string {
  return `${companyAttendancePath(companyId)}/end-day`;
}

export function companyAttendanceByIdPath(companyId: string, attendanceId: string): string {
  return `${companyAttendancePath(companyId)}/${attendanceId}`;
}

export function companyAttendanceMePath(companyId: string): string {
  return `${companyAttendancePath(companyId)}/me`;
}

export function companyTimeCorrectionRequestsPath(companyId: string): string {
  return `${companyAttendancePath(companyId)}/time-correction-requests`;
}

export function companyTimeCorrectionRequestByIdPath(companyId: string, requestId: string): string {
  return `${companyTimeCorrectionRequestsPath(companyId)}/${requestId}`;
}

export function companyTimeCorrectionRequestApprovePath(
  companyId: string,
  requestId: string
): string {
  return `${companyTimeCorrectionRequestByIdPath(companyId, requestId)}/approve`;
}

export function companyTimeCorrectionRequestRejectPath(
  companyId: string,
  requestId: string
): string {
  return `${companyTimeCorrectionRequestByIdPath(companyId, requestId)}/reject`;
}

export function companyShiftAssignmentsPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/shift-assignments`;
}

export function companyShiftAssignmentsBulkPath(companyId: string): string {
  return `${companyShiftAssignmentsPath(companyId)}/bulk`;
}

export function companyShiftAssignmentByIdPath(companyId: string, assignmentId: string): string {
  return `${companyShiftAssignmentsPath(companyId)}/${assignmentId}`;
}

export function companyShiftAssignmentConfirmPath(companyId: string, assignmentId: string): string {
  return `${companyShiftAssignmentByIdPath(companyId, assignmentId)}/confirm`;
}

export function companyShiftAssignmentCancelPath(companyId: string, assignmentId: string): string {
  return `${companyShiftAssignmentByIdPath(companyId, assignmentId)}/cancel`;
}

export function companyShiftAssignmentsMePath(companyId: string): string {
  return `${companyShiftAssignmentsPath(companyId)}/me`;
}

export function companyShiftRotaUploadPreviewPath(companyId: string): string {
  return `${companyShiftAssignmentsPath(companyId)}/rota-upload/preview`;
}

export function companyShiftRotaUploadCommitPath(companyId: string): string {
  return `${companyShiftAssignmentsPath(companyId)}/rota-upload/commit`;
}

export function companyShiftAssignmentsRecentCustomSlotsPath(companyId: string): string {
  return `${companyShiftAssignmentsPath(companyId)}/recent-custom-slots`;
}

export function companyShiftAlertByIdPath(companyId: string, alertId: string): string {
  return `${COMPANIES}/${companyId}/shift-alerts/${alertId}`;
}

export function companyShiftAlertAcknowledgePath(companyId: string, alertId: string): string {
  return `${companyShiftAlertByIdPath(companyId, alertId)}/acknowledge`;
}

export function companyShiftsPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/shifts`;
}

export function companyShiftsTemplatesPath(companyId: string): string {
  return `${companyShiftsPath(companyId)}/templates`;
}
/** Cross-company aggregate endpoints — single round trip across N companies. */
export const SHIFTS_OVERVIEW = '/shifts-overview';
export const SHIFT_ASSIGNMENTS_OVERVIEW = '/shift-assignments-overview';
/** Self-service shift assignments. Backend auto-scopes to the caller's memberships. */
export const SHIFT_ASSIGNMENTS_ME = '/shift-assignments/me';
/** Members + assignments in one call — use to render rows for staff with no shifts in range. */
export const SHIFTS_ROSTER = '/shifts-roster';

/** Operator workspace endpoint: cross-company, gated by attendance:read_team. */
export const COMPANY_MEMBERS = '/company-members';
export const ATTENDANCE = '/attendance';
export const TIME_CORRECTIONS = '/attendance/time-corrections';
export const BONUSES = '/bonuses';
export const PENALTIES = '/penalties';
/** Self-service /me list endpoints. Backend auto-scopes to the caller's memberships. */
export const ATTENDANCE_ME = '/attendance/me';
export const ATTENDANCE_ME_TODAY = '/attendance/me/today';
export const ATTENDANCE_ME_TIME_CORRECTIONS = '/attendance/me/time-corrections';
export const BONUSES_ME = '/bonuses/me';
export const LEAVES = '/leaves';
export const LEAVES_ME = '/leaves/me';
export const LEAVE_TYPES = '/leave-types';
export function leaveTypeByIdPath(leaveTypeId: string): string {
  return `${LEAVE_TYPES}/${leaveTypeId}`;
}
export const SECURITY_LEDGER_ME = '/security-ledger/me';
export const SESSION_WALLBOARD = '/session-wallboard';
export const SETUP_WIZARD_STATUS = '/setup-wizard/status';
export const SETUP_WIZARD_COMPANY_STATUSES = '/setup-wizard/company-statuses';
export const SETUP_WIZARD_COMPANY_SETUP = '/setup-wizard/company-setup';
export const SETUP_WIZARD_DRAFTS = '/setup-wizard/drafts';
export const SETUP_WIZARD_IMPORTS = '/setup-wizard/imports';
export const SETUP_WIZARD_IMPORT_HISTORY = '/setup-wizard/imports/history';
export const SETUP_WIZARD_IMPORT_TEMPLATE = '/setup-wizard/imports/template';
export const SETUP_WIZARD_DEMO_DATA = '/setup-wizard/demo-data';

export function setupWizardImportPath(id: string): string {
  return `${SETUP_WIZARD_IMPORTS}/${id}`;
}

export function setupWizardImportMappingPath(id: string): string {
  return `${setupWizardImportPath(id)}/mapping`;
}

export function setupWizardImportValidatePath(id: string): string {
  return `${setupWizardImportPath(id)}/validate`;
}

export function setupWizardImportCommitPath(id: string): string {
  return `${setupWizardImportPath(id)}/commit`;
}

/** `set-aside-failed`, `restore-set-aside` or `set-aside.csv` on an import. */
export function setupWizardImportSetAsidePath(
  id: string,
  action: 'set-aside-failed' | 'restore-set-aside' | 'set-aside.csv'
): string {
  return `${setupWizardImportPath(id)}/${action}`;
}

export function setupWizardDraftPath(id: string): string {
  return `${SETUP_WIZARD_DRAFTS}/${id}`;
}

export function setupWizardDraftImportsPath(id: string): string {
  return `${setupWizardDraftPath(id)}/imports`;
}

export function setupWizardDraftImportCheckPath(id: string, importId: string): string {
  return `${setupWizardDraftImportsPath(id)}/${importId}/check-rows`;
}

export function setupWizardDraftImportSetAsidePath(
  id: string,
  importId: string,
  action: 'set-aside-failed' | 'restore-set-aside' | 'set-aside.csv'
): string {
  return `${setupWizardDraftImportsPath(id)}/${importId}/${action}`;
}

export function setupWizardDraftLaunchPath(id: string): string {
  return `${setupWizardDraftPath(id)}/launch`;
}

export function companyShiftByIdPath(companyId: string, shiftId: string): string {
  return `${companyShiftsPath(companyId)}/${shiftId}`;
}

export function companyLeavesPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/leaves`;
}

export function companyLeaveByIdPath(companyId: string, leaveId: string): string {
  return `${companyLeavesPath(companyId)}/${leaveId}`;
}

export function companyLeaveDecisionPath(companyId: string, leaveId: string): string {
  return `${companyLeaveByIdPath(companyId, leaveId)}/decision`;
}

export function companyLeaveReplacementPath(companyId: string, leaveId: string): string {
  return `${companyLeaveByIdPath(companyId, leaveId)}/replacement`;
}

export const PAYROLL = '/payroll';

// ─── Payroll Runs (company-scoped) ─────────────────
export function companyPayrollRunsPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/payroll/runs`;
}
export function companyPayrollRunPath(companyId: string, runId: string): string {
  return `${companyPayrollRunsPath(companyId)}/${runId}`;
}
export function companyPayrollRunPreflightPath(companyId: string, runId: string): string {
  return `${companyPayrollRunPath(companyId, runId)}/preflight`;
}
export function companyPayrollRunCalculatePath(companyId: string, runId: string): string {
  return `${companyPayrollRunPath(companyId, runId)}/calculate`;
}
export function companyPayrollRunSubmitPath(companyId: string, runId: string): string {
  return `${companyPayrollRunPath(companyId, runId)}/submit`;
}
export function companyPayrollRunApprovePath(companyId: string, runId: string): string {
  return `${companyPayrollRunPath(companyId, runId)}/approve`;
}
export function companyPayrollRunRejectPath(companyId: string, runId: string): string {
  return `${companyPayrollRunPath(companyId, runId)}/reject`;
}
export function companyPayrollRunVoidPath(companyId: string, runId: string): string {
  return `${companyPayrollRunPath(companyId, runId)}/void`;
}
export function companyPayrollRunAdjustPath(companyId: string, runId: string): string {
  return `${companyPayrollRunPath(companyId, runId)}/adjust`;
}
export function companyPayrollRunItemsPath(companyId: string, runId: string): string {
  return `${companyPayrollRunPath(companyId, runId)}/items`;
}
export function companyPayrollRunItemPath(
  companyId: string,
  runId: string,
  itemId: string
): string {
  return `${companyPayrollRunItemsPath(companyId, runId)}/${itemId}`;
}
export function companyPayrollRunPreviewPath(companyId: string, runId: string): string {
  return `${companyPayrollRunPath(companyId, runId)}/preview`;
}
export function companyPayrollSummaryPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/payroll/summary`;
}
export function companyPayrollLivePath(companyId: string): string {
  return `${COMPANIES}/${companyId}/payroll/live`;
}
export function companyPayrollEmployeeLivePath(companyId: string, employeeId: string): string {
  return `${companyPayrollLivePath(companyId)}/${employeeId}`;
}

// ─── Org Payroll Batches ───────────────────────────
export const PAYROLL_BATCHES = '/payroll/batches';
export const PAYROLL_BATCH_READINESS = '/payroll/batches/readiness';
export const PAYROLL_BATCH_DASHBOARD = '/payroll/batches/dashboard';
export const PAYROLL_BATCHES_FINALIZE = '/payroll/batches/finalize';
export const PAYROLL_BATCH_FINALIZE_PREVIEW = '/payroll/batches/finalize-preview';
export const PAYROLL_BATCH_BULK_CREATE_RUNS = '/payroll/batches/bulk-create-runs';
export const PAYROLL_BATCH_BULK_CALCULATE_DRAFTS = '/payroll/batches/bulk-calculate-drafts';
export const PAYROLL_BATCH_BULK_SUBMIT_CALCULATED = '/payroll/batches/bulk-submit-calculated';
export const PAYROLL_BATCH_BULK_APPROVE_PENDING = '/payroll/batches/bulk-approve-pending';

export function payrollBatchPath(batchId: string): string {
  return `${PAYROLL_BATCHES}/${batchId}`;
}
export function payrollBatchPreviewPath(batchId: string): string {
  return `${payrollBatchPath(batchId)}/preview`;
}
export function payrollBatchSummaryPath(batchId: string): string {
  return `${payrollBatchPath(batchId)}/summary`;
}
export function payrollBatchItemsPath(batchId: string): string {
  return `${payrollBatchPath(batchId)}/items`;
}
export function payrollBatchItemPath(batchId: string, employeeId: string): string {
  return `${payrollBatchItemsPath(batchId)}/${employeeId}`;
}
export function payrollBatchConsolidatePath(batchId: string): string {
  return `${payrollBatchPath(batchId)}/consolidate`;
}
export function payrollBatchApprovePath(batchId: string): string {
  return `${payrollBatchPath(batchId)}/approve`;
}
export function payrollBatchMarkPaidPath(batchId: string): string {
  return `${payrollBatchPath(batchId)}/mark-paid`;
}
export function payrollBatchRejectPath(batchId: string): string {
  return `${payrollBatchPath(batchId)}/reject`;
}
export function payrollBatchResubmitPath(batchId: string): string {
  return `${payrollBatchPath(batchId)}/resubmit`;
}
export function payrollBatchVoidPath(batchId: string): string {
  return `${payrollBatchPath(batchId)}/void`;
}

// ─── Salary Slips (admin) ──────────────────────────
export function payrollBatchSlipsPath(batchId: string): string {
  return `${payrollBatchPath(batchId)}/slips`;
}
export function payrollBatchSlipPath(batchId: string, employeeId: string): string {
  return `${payrollBatchSlipsPath(batchId)}/${employeeId}`;
}
export function payrollBatchSlipPdfPath(batchId: string, employeeId: string): string {
  return `${payrollBatchSlipPath(batchId, employeeId)}/pdf`;
}
export function payrollBatchSlipExcelPath(batchId: string, employeeId: string): string {
  return `${payrollBatchSlipPath(batchId, employeeId)}/excel`;
}
export function payrollBatchSlipDocxPath(batchId: string, employeeId: string): string {
  return `${payrollBatchSlipPath(batchId, employeeId)}/docx`;
}
export function payrollBatchSlipSettlementPath(batchId: string, employeeId: string): string {
  return `${payrollBatchSlipPath(batchId, employeeId)}/settlement`;
}

// ─── Self-Service ──────────────────────────────────
export const PAYROLL_ME_SLIPS = '/payroll/me/slips';
export const PAYROLL_ME_CURRENT = '/payroll/me/current';
export const PAYROLL_ME_HISTORY = '/payroll/me/history';
export const PAYROLL_ME_LIVE = '/payroll/me/live';
export const PAYROLL_CALCULATE = '/payroll/calculate';
export const PAYROLL_CALCULATE_SLIP_PREVIEW_HTML = '/payroll/calculate/slip-preview/html';
export const PAYROLL_PAY = '/payroll/pay';
export const PAYROLL_PAYMENTS = '/payroll/payments';

export function payrollPaymentPath(snapshotId: string): string {
  return `${PAYROLL_PAYMENTS}/${snapshotId}`;
}

export function payrollPaymentItemPath(snapshotId: string, itemId: string): string {
  return `${payrollPaymentPath(snapshotId)}/items/${itemId}`;
}

export function payrollPaymentItemPdfPath(snapshotId: string, itemId: string): string {
  return `${payrollPaymentItemPath(snapshotId, itemId)}/pdf`;
}

/** `slipId`: an org batch id or a live payment snapshot id (see /payroll/me/slips). */
export function payrollMeSlipPath(slipId: string): string {
  return `${PAYROLL_ME_SLIPS}/${slipId}`;
}
export function payrollMeSlipViewPath(slipId: string): string {
  return `${payrollMeSlipPath(slipId)}/view`;
}
export function payrollMeSlipPdfPath(slipId: string): string {
  return `${payrollMeSlipPath(slipId)}/pdf`;
}
export function payrollMeSlipExcelPath(slipId: string): string {
  return `${payrollMeSlipPath(slipId)}/excel`;
}

// ─── Tax Rules ─────────────────────────────────────
export const TAX_RULES = '/tax-rules';
export const TAX_RULES_CALCULATE_PREVIEW = '/tax-rules/calculate-preview';
export const TAX_RULES_EXCHANGE_RATES = '/tax-rules/exchange-rates';

export function taxRulePath(id: string): string {
  return `${TAX_RULES}/${id}`;
}
export function taxRuleDeactivatePath(id: string): string {
  return `${taxRulePath(id)}/deactivate`;
}

export const UPLOAD = '/upload';

export function uploadByIdPath(id: string): string {
  return `${UPLOAD}/${id}`;
}

export function uploadDownloadPath(id: string): string {
  return `${uploadByIdPath(id)}/download`;
}

export function uploadExpiryPath(id: string): string {
  return `${uploadByIdPath(id)}/expiry`;
}

export const UPLOAD_SUMMARY = `${UPLOAD}/summary`;
export const UPLOAD_ALL_STAFF = `${UPLOAD}/staff/all`;

export const DRIVE_SUMMARY = '/drive/summary';
export const DRIVE_UNPLACED = '/drive/unplaced';

export function driveFoldersPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/drive/folders`;
}

export function driveFolderPath(companyId: string, id: string): string {
  return `${driveFoldersPath(companyId)}/${id}`;
}

export function driveFilesPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/drive/files`;
}

export function driveFilesMovePath(companyId: string): string {
  return `${driveFilesPath(companyId)}/move`;
}

export function companyUploadPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/upload`;
}

export function companyUploadByIdPath(companyId: string, id: string): string {
  return `${companyUploadPath(companyId)}/${id}`;
}

export function companyUploadArchivePath(companyId: string, id: string): string {
  return `${companyUploadByIdPath(companyId, id)}/archive`;
}

export function companyUploadUnarchivePath(companyId: string, id: string): string {
  return `${companyUploadByIdPath(companyId, id)}/unarchive`;
}

export function companyUploadExpiryPath(companyId: string, id: string): string {
  return `${companyUploadByIdPath(companyId, id)}/expiry`;
}
export function companyPenaltiesPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/penalties`;
}

export function companyPenaltyTypesPath(companyId: string): string {
  return `${companyPenaltiesPath(companyId)}/types`;
}

export function companyPenaltyEntryApprovePath(companyId: string, entryId: string): string {
  return `${companyPenaltiesPath(companyId)}/${entryId}/approve`;
}

export function companyPenaltyEntryReversePath(companyId: string, entryId: string): string {
  return `${companyPenaltiesPath(companyId)}/${entryId}/reverse`;
}

export function companyBonusesPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/bonuses`;
}

export function companyBonusApprovePath(companyId: string, id: string): string {
  return `${companyBonusesPath(companyId)}/${id}/approve`;
}

export function companyBonusReversePath(companyId: string, id: string): string {
  return `${companyBonusesPath(companyId)}/${id}/reverse`;
}

// ─── Expenses ──────────────────────────────────────
/** Cross-company workspace list (admin). Backend scopes to companies the caller can read. */
export const EXPENSES = '/expenses';
/** Aggregated analytics for charts (admin). */
export const EXPENSE_ANALYTICS = '/expenses/analytics';
/** Excel export of expenses (honors the same filters as the workspace list). */
export const EXPENSE_EXPORT_EXCEL = '/expenses/export/excel';

export function companyExpensesPath(companyId: string): string {
  return `${COMPANIES}/${companyId}/expenses`;
}

export function companyExpensePath(companyId: string, id: string): string {
  return `${companyExpensesPath(companyId)}/${id}`;
}

/** Organization expense categories (they are not per company). */
/** A receipt or invoice of a ledger expense, read through its access checks. */
export function expenseAttachmentPath(expenseId: string, uploadId: string): string {
  return `${EXPENSES}/${expenseId}/attachments/${uploadId}`;
}
export const EXPENSE_CATEGORIES = '/expenses/categories';
/** Organization expenses: the organization itself carries them, no company. */
export const ORGANIZATION_EXPENSES = '/expenses/organization';

// ─── Expense claims (staff submit; manager then admin approve) ─────
/** The caller's own claims (list, submit). */
export const MY_EXPENSE_CLAIMS = '/me/expenses';
/** Categories and companies (with pay currency) a claim can use. */
export const MY_EXPENSE_CLAIM_OPTIONS = '/me/expenses/options';
/** Approvals inbox for expenses:approve holders. */
export const EXPENSE_APPROVALS = '/expenses/approvals';

export function myExpenseClaimWithdrawPath(id: string): string {
  return `${MY_EXPENSE_CLAIMS}/${id}/withdraw`;
}

export function myExpenseClaimReceiptPath(id: string, uploadId: string): string {
  return `${MY_EXPENSE_CLAIMS}/${id}/receipts/${uploadId}`;
}

export function expenseClaimPath(id: string): string {
  return `/expenses/claims/${id}`;
}

export function expenseClaimReceiptPath(id: string, uploadId: string): string {
  return `${expenseClaimPath(id)}/receipts/${uploadId}`;
}

export function companySecurityLedgerBase(companyId: string): string {
  return `${COMPANIES}/${companyId}/security-ledger`;
}

export function companySecurityLedgerBalancePath(companyId: string, employeeId: string): string {
  return `${companySecurityLedgerBase(companyId)}/employees/${employeeId}/balance`;
}

export function companySecurityLedgerBalanceSummaryPath(
  companyId: string,
  employeeId: string
): string {
  return `${companySecurityLedgerBase(companyId)}/employees/${employeeId}/balance-summary`;
}

export function companySecurityLedgerTransactionsPath(
  companyId: string,
  employeeId: string
): string {
  return `${companySecurityLedgerBase(companyId)}/employees/${employeeId}/transactions`;
}

export function companySecurityLedgerDeductionsPath(companyId: string): string {
  return `${companySecurityLedgerBase(companyId)}/deductions`;
}

export function companySecurityLedgerAdjustmentsPath(companyId: string): string {
  return `${companySecurityLedgerBase(companyId)}/adjustments`;
}

export function companySecurityLedgerMonthlyDeductionPath(
  companyId: string,
  employeeId: string
): string {
  return `${companySecurityLedgerBase(companyId)}/employees/${employeeId}/monthly-deduction`;
}

export function companySecurityLedgerRefundRequestsPath(
  companyId: string,
  employeeId: string
): string {
  return `${companySecurityLedgerBase(companyId)}/employees/${employeeId}/refund-requests`;
}

/** NOTE: :txId in this path must be the Mongo document `_id` (not the UUID `txId` field). */
export function companySecurityLedgerApproveRefundPath(
  companyId: string,
  txMongoId: string
): string {
  return `${companySecurityLedgerBase(companyId)}/transactions/${txMongoId}/approve-refund`;
}

/** NOTE: :txId in this path must be the Mongo document `_id` (not the UUID `txId` field). */
export function companySecurityLedgerRejectRefundPath(
  companyId: string,
  txMongoId: string
): string {
  return `${companySecurityLedgerBase(companyId)}/transactions/${txMongoId}/reject-refund`;
}

export const NOTIFICATIONS = '/notifications';
export const NOTIFICATION_TEMPLATES = '/notification-templates';

export const ROLES = '/roles';
export const ROLES_PERMISSIONS_ALL = '/roles/permissions/all';
export const ROLES_TEMPLATES = '/roles/templates';

/** Self-service: penalties applied to the current user across their member companies. */
export const PENALTIES_ME = '/penalties/me';

export const AUDIT_LOGS = '/audit-logs';
export const REPORTS_COMPLIANCE = '/reports/compliance';
export const REPORTS_COMPLIANCE_SUMMARY = '/reports/compliance/summary';
export const REPORTS_COMPLIANCE_EXPORT_CSV = '/reports/compliance/export/csv';
export const REPORTS_COMPLIANCE_EXPORT_EXCEL = '/reports/compliance/export/excel';

export function rolePermissionsPath(roleId: string): string {
  return `${ROLES}/${roleId}/permissions`;
}

export function auditLogByIdPath(id: string): string {
  return `${AUDIT_LOGS}/${id}`;
}

export function auditLogsExportCsvPath(): string {
  return `${AUDIT_LOGS}/export/csv`;
}

export function notificationsUnreadCountPath(): string {
  return `${NOTIFICATIONS}/unread-count`;
}

export function notificationCompanyFacetsPath(): string {
  return `${NOTIFICATIONS}/company-facets`;
}

export function notificationMarkReadPath(id: string): string {
  return `${NOTIFICATIONS}/${id}/read`;
}

export function notificationTemplateByIdPath(id: string): string {
  return `${NOTIFICATION_TEMPLATES}/${id}`;
}

// ─── Announcements ──────────────────────────────────
export const ANNOUNCEMENTS = '/announcements';
export const ANNOUNCEMENTS_BANNER = '/announcements/banner';

/**
 * Announcements from the platform operator, not from this tenant's own
 * administrators. A separate prefix rather than a route under `/announcements`,
 * where the tenant controller already owns `:id`.
 */
export const PLATFORM_ANNOUNCEMENTS_ACTIVE = '/platform-announcements/active';

export function platformAnnouncementDismissPath(id: string): string {
  return `/platform-announcements/${id}/dismiss`;
}

export function announcementByIdPath(id: string): string {
  return `${ANNOUNCEMENTS}/${id}`;
}

export function announcementPublishPath(id: string): string {
  return `${ANNOUNCEMENTS}/${id}/publish`;
}

export function announcementArchivePath(id: string): string {
  return `${ANNOUNCEMENTS}/${id}/archive`;
}

export function announcementDismissPath(id: string): string {
  return `${ANNOUNCEMENTS}/${id}/dismiss`;
}

// ─── Support tickets (Help & support) ─────────────
export const SUPPORT_TICKETS = '/support-tickets';

export function supportTicketByIdPath(id: string): string {
  return `${SUPPORT_TICKETS}/${id}`;
}

export function supportTicketMessagesPath(id: string): string {
  return `${SUPPORT_TICKETS}/${id}/messages`;
}

export function supportTicketResolvePath(id: string): string {
  return `${SUPPORT_TICKETS}/${id}/resolve`;
}

// ─── Channels (team chat) ─────────────────────────
export const CHANNELS = '/channels';

export function channelByIdPath(channelId: string): string {
  return `${CHANNELS}/${channelId}`;
}

export function channelMembersPath(channelId: string): string {
  return `${channelByIdPath(channelId)}/members`;
}

export function channelMessagesPath(channelId: string): string {
  return `${channelByIdPath(channelId)}/messages`;
}

export function channelMessagePath(channelId: string, messageId: string): string {
  return `${channelMessagesPath(channelId)}/${messageId}`;
}

// ─── Boards (Kanban) ───────────────────────────────
export const BOARDS = '/boards';

export function boardByIdPath(id: string): string {
  return `${BOARDS}/${id}`;
}

export function boardMembersPath(boardId: string): string {
  return `${BOARDS}/${boardId}/members`;
}

export function boardMemberByUserPath(boardId: string, userId: string): string {
  return `${boardMembersPath(boardId)}/${userId}`;
}

export function boardListsPath(boardId: string): string {
  return `${BOARDS}/${boardId}/lists`;
}

export function boardListsReorderPath(boardId: string): string {
  return `${boardListsPath(boardId)}/reorder`;
}

export function boardListByIdPath(boardId: string, listId: string): string {
  return `${boardListsPath(boardId)}/${listId}`;
}

export function boardCardsPath(boardId: string): string {
  return `${BOARDS}/${boardId}/cards`;
}

export function boardCardByIdPath(boardId: string, cardId: string): string {
  return `${boardCardsPath(boardId)}/${cardId}`;
}

export function boardCardMovePath(boardId: string, cardId: string): string {
  return `${boardCardByIdPath(boardId, cardId)}/move`;
}

export function boardCardAttachmentsPath(boardId: string, cardId: string): string {
  return `${boardCardByIdPath(boardId, cardId)}/attachments`;
}

export function boardCardAttachmentPath(
  boardId: string,
  cardId: string,
  attachmentId: string
): string {
  return `${boardCardAttachmentsPath(boardId, cardId)}/${attachmentId}`;
}

export function boardCardCommentsPath(boardId: string, cardId: string): string {
  return `${boardCardByIdPath(boardId, cardId)}/comments`;
}

export function boardCardChecklistsPath(boardId: string, cardId: string): string {
  return `${boardCardByIdPath(boardId, cardId)}/checklists`;
}

export function boardCardChecklistPath(
  boardId: string,
  cardId: string,
  checklistId: string
): string {
  return `${boardCardChecklistsPath(boardId, cardId)}/${checklistId}`;
}

export function boardCardChecklistItemsPath(
  boardId: string,
  cardId: string,
  checklistId: string
): string {
  return `${boardCardChecklistsPath(boardId, cardId)}/${checklistId}/items`;
}

export function boardCardChecklistItemPath(
  boardId: string,
  cardId: string,
  checklistId: string,
  itemId: string
): string {
  return `${boardCardChecklistItemsPath(boardId, cardId, checklistId)}/${itemId}`;
}

export function boardCardActivitiesPath(boardId: string, cardId: string): string {
  return `${boardCardByIdPath(boardId, cardId)}/activities`;
}

export function boardLabelsPath(boardId: string): string {
  return `${boardCardsPath(boardId)}/labels`;
}

export function boardLabelByIdPath(boardId: string, labelId: string): string {
  return `${boardLabelsPath(boardId)}/${labelId}`;
}

export const SUPPORT_SESSIONS = {
  EXCHANGE: '/support-sessions/exchange',
  VISIBLE: '/support-sessions/visible',
  end: (id: string) => `/support-sessions/${id}/end`,
} as const;
